import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import {
  CheckCircle,
  XCircle,
  ChartLineUp,
  Clock,
  WarningCircle,
  Receipt,
  ShieldWarning,
  Buildings,
  ThumbsUp,
  ThumbsDown,
} from "@phosphor-icons/react";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const APPROVAL_META = {
  pending_approval: { label: "Pending", color: "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" },
  pending_admin_review: { label: "Admin Review", color: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]" },
  approved: { label: "Approved", color: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  admin_approved: { label: "Admin Override", color: "bg-[#E0E7FF] text-[#3730A3] border-[#A5B4FC]" },
  rejected: { label: "Rejected", color: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
};

export default function ApprovalSummary() {
  const { activeProject, activeProjectId } = useProject();
  const [data, setData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeProjectId) return;
    (async () => {
      setLoading(true);
      try {
        const [sumRes, fcRes] = await Promise.all([
          api.get("/pos/approval-summary"),
          api.get("/boq/forecast", { params: { project_id: activeProjectId } }),
        ]);
        setData(sumRes.data);
        setForecast(fcRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeProjectId]);

  if (loading || !data) {
    return <div className="p-10 text-center text-neutral-400">Loading approval summary…</div>;
  }

  const { po_by_status, total_pos, acceptance_rate, invoices_by_status, recent_decisions, top_accepted_vendors } = data;
  const accepted = po_by_status.accepted;
  const rejected = po_by_status.rejected;
  const partial = po_by_status.partially_fulfilled;
  const open_ = po_by_status.open;

  return (
    <div className="p-8 sm:p-10 max-w-[1600px] mx-auto" data-testid="approval-summary-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2 flex items-center gap-2">
            {activeProject && (
              <>
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: activeProject.color || "#0047FF" }} />
                <span>{activeProject.name}</span>
                <span className="text-neutral-300">·</span>
              </>
            )}
            <span>Compliance Overview</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight flex items-center gap-3">
            <ChartLineUp size={36} className="text-primary" weight="fill" />
            Approval Summary
          </h1>
          <p className="text-sm text-neutral-500 mt-2">
            PO acceptance, rejection and invoice approval health for this project at a glance.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">Acceptance Rate</div>
          <div className="font-display text-5xl font-bold tabular-nums text-[#137333]" data-testid="acceptance-rate">
            {acceptance_rate}%
          </div>
          <div className="text-xs text-neutral-500 mt-1">of decided POs (excl. open)</div>
        </div>
      </div>

      {/* Accept vs Reject big cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <BigCard
          testId="accepted-card"
          icon={<CheckCircle size={28} weight="fill" />}
          label="Accepted POs"
          count={accepted.count}
          value={fmtINR(accepted.value)}
          accentBg="bg-[#E6F4EA]"
          accentText="text-[#137333]"
          accentBorder="border-[#A4D0B3]"
          sub="Cumulative deliveries cover PO qty across every line"
        />
        <BigCard
          testId="rejected-card"
          icon={<XCircle size={28} weight="fill" />}
          label="Rejected POs"
          count={rejected.count}
          value={fmtINR(rejected.value)}
          accentBg="bg-[#FCE8E6]"
          accentText="text-[#C5221F]"
          accentBorder="border-[#F3B2AC]"
          sub="Over-delivery or price mismatches blocked these POs"
        />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <SmallStat testId="stat-total-pos" icon={<Receipt size={18} />} label="Total POs" value={total_pos} sub="All statuses combined" accent="text-neutral-900" />
        <SmallStat testId="stat-partial" icon={<WarningCircle size={18} />} label="Partially Fulfilled" value={partial.count} sub={fmtINR(partial.value)} accent="text-[#B06000]" />
        <SmallStat testId="stat-open" icon={<Clock size={18} />} label="Open / In-flight" value={open_.count} sub={fmtINR(open_.value)} accent="text-[#1967D2]" />
        <SmallStat testId="stat-pending-review" icon={<ShieldWarning size={18} />} label="Invoices: Admin Review" value={invoices_by_status?.pending_admin_review || 0} sub="Awaiting manual decision" accent="text-[#B06000]" />
      </div>

      {/* Bento: invoice breakdown + top vendors + recent decisions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Invoice approval mix */}
        <div className="bg-white border border-neutral-200 rounded-sm">
          <div className="px-6 py-4 border-b border-neutral-200">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Invoice Decisions</div>
            <div className="font-display text-lg font-bold mt-0.5">Approval Mix</div>
          </div>
          <div className="p-4 space-y-2.5">
            {Object.keys(APPROVAL_META).map((key) => {
              const count = invoices_by_status?.[key] || 0;
              const total = Object.values(invoices_by_status || {}).reduce((s, c) => s + c, 0);
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              const meta = APPROVAL_META[key];
              const barColor = key === "approved" || key === "admin_approved"
                ? "bg-[#137333]"
                : key === "rejected"
                ? "bg-[#C5221F]"
                : key === "pending_admin_review"
                ? "bg-[#B06000]"
                : "bg-primary";
              return (
                <div key={key} data-testid={`invoice-mix-${key}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${meta.color}`}>
                      {meta.label}
                    </span>
                    <span className="tabular-nums text-neutral-700 font-medium">{count}</span>
                  </div>
                  <div className="h-1.5 bg-neutral-100 overflow-hidden">
                    <div className={`h-full ${barColor}`} style={{ width: `${pct}%`, transition: "width 400ms ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top accepted vendors */}
        <div className="bg-white border border-neutral-200 rounded-sm">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Compliance Leaders</div>
              <div className="font-display text-lg font-bold mt-0.5">Top Accepted Vendors</div>
            </div>
            <Buildings size={18} className="text-neutral-400" />
          </div>
          <div className="p-4">
            {top_accepted_vendors?.length ? (
              <div className="space-y-2.5">
                {top_accepted_vendors.map((v) => {
                  const max = top_accepted_vendors[0]?.value || 1;
                  const pct = Math.round((v.value / max) * 100);
                  return (
                    <div key={v.vendor || "unknown"} data-testid={`top-vendor-${v.vendor}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-neutral-800 truncate pr-2">{v.vendor || "(unknown)"}</span>
                        <span className="text-neutral-500 tabular-nums text-xs whitespace-nowrap">{v.count} · {fmtINR(v.value)}</span>
                      </div>
                      <div className="h-1.5 bg-neutral-100 overflow-hidden">
                        <div className="h-full bg-[#137333]" style={{ width: `${pct}%`, transition: "width 400ms ease" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-neutral-400 py-6 text-center">No accepted POs yet for this project</div>
            )}
          </div>
        </div>

        {/* Recent decisions */}
        <div className="bg-white border border-neutral-200 rounded-sm">
          <div className="px-6 py-4 border-b border-neutral-200">
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Audit Trail</div>
            <div className="font-display text-lg font-bold mt-0.5">Recent Decisions</div>
          </div>
          <div className="divide-y divide-neutral-100 max-h-[400px] overflow-y-auto">
            {recent_decisions?.length ? recent_decisions.map((d) => {
              const meta = APPROVAL_META[d.approval_status] || APPROVAL_META.pending_approval;
              const isApproved = d.approval_status === "approved" || d.approval_status === "admin_approved";
              return (
                <Link
                  to={`/pos/${d.po_id}`}
                  key={d.invoice_id}
                  className="p-3 hover:bg-neutral-50 transition-colors flex items-start gap-3 block"
                  data-testid={`recent-decision-${d.invoice_id}`}
                >
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isApproved ? "bg-[#E6F4EA] text-[#137333]" : "bg-[#FCE8E6] text-[#C5221F]"
                  }`}>
                    {isApproved ? <ThumbsUp size={14} weight="fill" /> : <ThumbsDown size={14} weight="fill" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono-num text-xs text-primary truncate">{d.po_number}</span>
                      <span className={`text-[9px] uppercase tracking-wider px-1 py-0.5 border ${meta.color}`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-700 truncate">{d.vendor_name || "—"} · {fmtINR(d.amount)}</div>
                    <div className="text-[10px] text-neutral-500 mt-0.5">
                      by {d.approved_by_name || "—"}
                      {d.approved_at && <span> · {new Date(d.approved_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                </Link>
              );
            }) : (
              <div className="text-sm text-neutral-400 py-8 text-center">No decisions logged yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Financial Forecast */}
      {forecast && (
        <div className="mt-10" data-testid="financial-forecast-section">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl font-bold flex items-center gap-2">
              <ChartLineUp size={22} className="text-primary" weight="fill" />
              Financial Forecast
            </h2>
            <div className="text-xs text-neutral-500 uppercase tracking-wider">
              {forecast.rows.length} categor{forecast.rows.length === 1 ? "y" : "ies"} · {forecast.totals.over_budget_count} over budget
            </div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600">
                  <tr>
                    <th className="text-left px-6 py-3 font-medium text-xs uppercase tracking-wider">Category</th>
                    <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Original BOQ Budget</th>
                    <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Budget as per PO Raised</th>
                    <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Pending Projections</th>
                    <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Projected Final Cost</th>
                    <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Δ vs Budget</th>
                  </tr>
                </thead>
                <tbody>
                  {forecast.rows.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12 text-neutral-400">
                      No BOQ items yet. Add categories to see the forecast.
                    </td></tr>
                  ) : forecast.rows.map((r) => (
                    <tr
                      key={r.category}
                      className={`border-t border-neutral-100 ${r.over_original_budget ? "bg-[#FCE8E6] hover:bg-[#F8D7D5]" : "hover:bg-neutral-50"}`}
                      data-testid={`forecast-row-${r.category}`}
                    >
                      <td className="px-6 py-3 text-neutral-900 font-medium">
                        {r.category}
                        {r.over_original_budget && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[#F3B2AC] bg-white text-[#C5221F]">
                            <WarningCircle size={11} weight="fill" /> OVERRUN
                          </span>
                        )}
                        {r.units.length > 0 && (
                          <div className="text-[10px] text-neutral-400 uppercase tracking-wider">{r.units.join(" · ")}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-neutral-700">{fmtINR(r.original_budget)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-neutral-900">{fmtINR(r.budget_as_per_po_raised)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-neutral-700">
                        {r.pending_projections > 0 ? fmtINR(r.pending_projections) : <span className="text-neutral-300">—</span>}
                      </td>
                      <td className={`px-4 py-3 text-right tabular-nums font-medium ${r.over_original_budget ? "text-[#C5221F]" : "text-neutral-900"}`}>
                        {fmtINR(r.projected_final_cost)}
                      </td>
                      <td className={`px-4 py-3 text-right tabular-nums text-xs ${r.over_original_budget ? "text-[#C5221F] font-medium" : "text-neutral-400"}`}>
                        {r.over_original_budget ? `+${fmtINR(r.overrun)}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {forecast.rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-neutral-200 bg-neutral-50 font-medium">
                      <td className="px-6 py-3 text-xs uppercase tracking-wider text-neutral-700">Totals</td>
                      <td className="px-4 py-3 text-right tabular-nums text-neutral-700">{fmtINR(forecast.totals.original_budget)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-neutral-900">{fmtINR(forecast.totals.budget_as_per_po_raised)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-neutral-700">{fmtINR(forecast.totals.pending_projections)}</td>
                      <td className={`px-4 py-3 text-right tabular-nums ${forecast.totals.overrun_total > 0 ? "text-[#C5221F]" : "text-neutral-900"}`}>
                        {fmtINR(forecast.totals.projected_final_cost)}
                      </td>
                      <td className={`px-4 py-3 text-right tabular-nums text-xs ${forecast.totals.overrun_total > 0 ? "text-[#C5221F]" : "text-neutral-400"}`}>
                        {forecast.totals.overrun_total > 0 ? `+${fmtINR(forecast.totals.overrun_total)}` : "—"}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BigCard({ icon, label, count, value, accentBg, accentText, accentBorder, sub, testId }) {
  return (
    <div className={`border rounded-sm p-6 ${accentBg} ${accentBorder}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className={`text-xs uppercase tracking-[0.18em] font-medium ${accentText}`}>{label}</div>
        <div className={accentText}>{icon}</div>
      </div>
      <div className="flex items-baseline gap-3 mb-2">
        <div className={`font-display text-6xl font-bold tabular-nums ${accentText}`}>{count}</div>
        <div className="text-sm text-neutral-700 tabular-nums">{value}</div>
      </div>
      <div className={`text-xs ${accentText} opacity-80`}>{sub}</div>
    </div>
  );
}

function SmallStat({ icon, label, value, sub, accent, testId }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-sm p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">{label}</div>
        <div className="h-7 w-7 bg-neutral-100 flex items-center justify-center text-neutral-700">{icon}</div>
      </div>
      <div className={`font-display text-3xl font-bold tabular-nums ${accent}`}>{value}</div>
      <div className="text-xs text-neutral-500 mt-1">{sub}</div>
    </div>
  );
}
